import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useStudentAuth } from '../../contexts/StudentAuthContext';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import SignaturePad from './SignaturePad';
import StudentOnboarding from './StudentOnboarding';
import { pushSupported, isStudentSubscribed, subscribeStudentPush, unsubscribeStudentPush } from '../../utils/studentPush';

const stages = [
  { key: 'registration', label: 'Registration', icon: 'fa-user-plus' },
  { key: 'application_submitted', label: 'Application', icon: 'fa-file-alt' },
  { key: 'offer_letter_received', label: 'Offer Letter', icon: 'fa-envelope-open-text' },
  { key: 'documentation_process', label: 'Documents', icon: 'fa-folder-open' },
  { key: 'ministry_approval', label: 'Ministry', icon: 'fa-landmark' },
  { key: 'visa_application_submitted', label: 'Visa Applied', icon: 'fa-passport' },
  { key: 'visa_decision', label: 'Visa Decision', icon: 'fa-stamp' },
  { key: 'ticket_travel_date', label: 'Ticket', icon: 'fa-plane-departure' },
  { key: 'student_flown', label: 'Flown', icon: 'fa-graduation-cap' },
];

// Map backend stages (includes both visa outcomes) to the student-visible display index.
// visa_approved + visa_denied both collapse into the "visa_decision" virtual stage.
const stageKeyToDisplayIdx = (backendKey) => {
  if (backendKey === 'visa_approved' || backendKey === 'visa_denied') {
    return stages.findIndex(s => s.key === 'visa_decision');
  }
  const idx = stages.findIndex(s => s.key === backendKey);
  return idx;
};

// E-Signature Canvas Component
const SignatureCanvas = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#192650';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDraw = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const submit = () => {
    if (!hasDrawn) { toast.error('Please draw your signature first'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Draw your signature below using mouse or finger</p>
      <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair touch-none"
          style={{ height: 200 }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={clear} className="px-4 py-2 text-xs border rounded-lg hover:bg-gray-50" data-testid="signature-clear-btn">
          <i className="fas fa-redo mr-1.5"></i>Redraw
        </button>
        <button onClick={submit} disabled={!hasDrawn} className="px-4 py-2 text-xs bg-[#192650] text-white rounded-lg disabled:opacity-40" data-testid="signature-submit-btn">
          <i className="fas fa-check mr-1.5"></i>Submit Signature
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700" data-testid="signature-cancel-btn">Cancel</button>
      </div>
    </div>
  );
};

// Password Change Modal
const PasswordChangeModal = ({ onClose, onSave }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await onSave(newPassword);
      toast.success('Password changed successfully!');
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="password-change-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
        <h3 className="text-base font-bold text-gray-800 mb-1"><i className="fas fa-key mr-2 text-amber-500"></i>Change Password</h3>
        <p className="text-xs text-gray-500 mb-4">Please set a new password for your account</p>
        <div className="space-y-3">
          <input type="password" placeholder="New password (min 6 characters)" value={newPassword} onChange={e => setNewPassword(e.target.value)}
            className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="new-password-input" />
          <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
            className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="confirm-password-input" />
          <button onClick={handleSave} disabled={saving} className="w-full bg-[#192650] text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="save-password-btn">
            {saving ? 'Saving...' : 'Set New Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

const StudentPortalDashboard = () => {
  const { student, logout, studentApi, changePassword } = useStudentAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('updates');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [agreements, setAgreements] = useState([]);
  const [signingAgreement, setSigningAgreement] = useState(null);
  const [expandedAgreementId, setExpandedAgreementId] = useState(null);  // accordion: only one open at a time
  const [openSignFor, setOpenSignFor] = useState({});  // agreementId -> bool: reveal canvas when true
  const [consentChecked, setConsentChecked] = useState({});
  const [showProfile, setShowProfile] = useState(false);
  const [signatures, setSignatures] = useState({});  // agreementId -> base64 image data URL
  const [invoices, setInvoices] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState(null);
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [uploadingReceiptFor, setUploadingReceiptFor] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updatesUnread, setUpdatesUnread] = useState(0);
  const [uploadingDocFor, setUploadingDocFor] = useState(null);
  const [preview, setPreview] = useState(null); // { doc, url, kind, loading }
  const updatesEndRef = useRef(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    fetchPortalData();
    // Check if password change required
    if (student?.must_change_password) setShowPasswordChange(true);
    // Reflect current push-subscription state on the bell
    if (pushSupported()) {
      isStudentSubscribed().then(setPushOn).catch(() => {});
    }
  }, []);

  const togglePush = async () => {
    if (!pushSupported()) {
      toast.error('Alerts are not supported on this device/browser.');
      return;
    }
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeStudentPush(studentApi);
        setPushOn(false);
        toast.success('Alerts turned off');
      } else {
        await subscribeStudentPush(studentApi);
        setPushOn(true);
        toast.success("Alerts on — we'll notify you about documents & agreements");
      }
    } catch (e) {
      toast.error(e.message || 'Could not update alerts');
    } finally {
      setPushBusy(false);
    }
  };

  const fetchPortalData = async () => {
    try {
      const [portalRes, msgRes, docsRes, agreeRes, invRes, schedRes, receiptsRes] = await Promise.all([
        studentApi.get('/api/student-auth/my-portal'),
        studentApi.get('/api/student-auth/my-messages'),
        studentApi.get('/api/student-auth/my-documents'),
        studentApi.get('/api/student-auth/my-agreements').catch(() => ({ data: { agreements: [] } })),
        studentApi.get('/api/student-auth/my-invoices/list').catch(() => ({ data: { payments: [] } })),
        studentApi.get('/api/student-auth/my-payment-schedule').catch(() => ({ data: null })),
        studentApi.get('/api/student-auth/my-payment-receipts').catch(() => ({ data: { receipts: [] } })),
      ]);
      setData(portalRes.data);
      setMessages(msgRes.data.messages || []);
      setDocRequests(docsRes.data.requests || []);
      setAgreements(agreeRes.data.agreements || []);
      setInvoices(invRes.data.payments || invRes.data.invoices || []);
      setPaymentSchedule(schedRes.data || null);
      setPaymentReceipts(receiptsRes.data?.receipts || []);
      fetchUpdates();
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  const uploadInvoiceReceipt = async (invoiceId, file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PDF, PNG, JPG, or WebP files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File exceeds 5 MB limit');
      return;
    }
    setUploadingReceiptFor(invoiceId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await studentApi.post(`/api/student-auth/my-invoices/${invoiceId}/receipt`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Receipt uploaded — finance team will verify it soon');
      const [invRes, receiptsRes] = await Promise.all([
        studentApi.get('/api/student-auth/my-invoices/list').catch(() => ({ data: { payments: [] } })),
        studentApi.get('/api/student-auth/my-payment-receipts').catch(() => ({ data: { receipts: [] } })),
      ]);
      setInvoices(invRes.data.payments || invRes.data.invoices || []);
      setPaymentReceipts(receiptsRes.data?.receipts || []);
      fetchUpdates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload receipt');
    } finally {
      setUploadingReceiptFor(null);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await studentApi.post('/api/student-auth/my-messages/send', {
        content: newMessage,
        category: 'general'
      });
      setNewMessage('');
      const res = await studentApi.get('/api/student-auth/my-messages');
      setMessages(res.data.messages || []);
      fetchUpdates();
      toast.success('Message sent');
    } catch { toast.error('Failed to send message'); }
    finally { setSendingMessage(false); }
  };

  const fetchUpdates = async () => {
    try {
      const res = await studentApi.get('/api/student-auth/updates');
      setUpdates(res.data.items || []);
      setUpdatesUnread(res.data.unread_count || 0);
    } catch { /* non-fatal */ }
  };

  // When the student is on the Updates tab, keep it marked seen (clears badge),
  // including right after a refetch — avoids a stale count flashing on the badge.
  useEffect(() => {
    if (activeTab === 'updates') {
      setUpdatesUnread(0);
      studentApi.post('/api/student-auth/updates/mark-seen').catch(() => {});
    }
  }, [activeTab, updates]);

  useEffect(() => {
    if (activeTab === 'updates' && updates.length && updatesEndRef.current) {
      updatesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [updates, activeTab]);

  // Live badge: poll the unread count every 60s so new items bump the badge
  // without a reload. Skips while the student is already viewing Updates.
  useEffect(() => {
    const timer = setInterval(async () => {
      if (activeTab === 'updates') return;
      try {
        const res = await studentApi.get('/api/student-auth/updates/unread-count');
        setUpdatesUnread(res.data.unread_count || 0);
      } catch { /* non-fatal */ }
    }, 60000);
    return () => clearInterval(timer);
  }, [activeTab]);

  // Student uploads a document straight from the Updates feed (against a request)
  const handleDocUploadForRequest = async (requestId, file) => {
    if (!file) return;
    setUploadingDocFor(requestId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (requestId) formData.append('request_id', requestId);
      await studentApi.post('/api/student-auth/my-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded');
      const docsRes = await studentApi.get('/api/student-auth/my-documents').catch(() => ({ data: { requests: [] } }));
      setDocRequests(docsRes.data.requests || []);
      fetchUpdates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDocFor(null);
    }
  };

  const handlePayInvoice = async (invoice) => {
    try {
      // Checkout loads only on Pay, never on staff or public appointment pages.
      const ensureRazorpay = () => new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve();
        const existing = document.querySelector('script[src*="checkout.razorpay.com"]');
        if (existing) {
          existing.addEventListener('load', resolve);
          existing.addEventListener('error', () => reject(new Error('Razorpay failed to load')));
          if (window.Razorpay) resolve();
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.async = true;
        s.onload = resolve;
        s.onerror = () => reject(new Error('Razorpay failed to load'));
        document.body.appendChild(s);
      });
      await ensureRazorpay();

      const res = await studentApi.post('/api/student-auth/payments/create-order', { invoice_id: invoice.id });
      const { order_id, amount, currency, key_id, student_name, student_email, student_phone } = res.data;
      
      const options = {
        key: key_id,
        amount,
        currency,
        order_id,
        name: "Patrick's Edu Care",
        description: `Invoice: ${invoice.document_number || invoice.id}`,
        handler: async (response) => {
          try {
            await studentApi.post('/api/student-auth/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success('Payment successful!');
            fetchPortalData();
          } catch { toast.error('Payment verification failed'); }
        },
        prefill: { name: student_name, email: student_email, contact: student_phone },
        theme: { color: '#192650' }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => toast.error('Payment failed. Please try again.'));
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleSignAgreement = async (agreementId) => {
    const sig = signatures[agreementId];
    if (!sig) {
      toast.error('Please draw your signature in the box before signing');
      return;
    }
    if (!consentChecked[agreementId]) {
      toast.error('Please tick the consent checkbox');
      return;
    }
    try {
      await studentApi.post(`/api/student-auth/my-agreements/${agreementId}/sign`, {
        agreed: true,
        signature_data: sig,
      });
      toast.success('Agreement signed successfully');
      setConsentChecked(prev => { const c = { ...prev }; delete c[agreementId]; return c; });
      setSignatures(prev => { const c = { ...prev }; delete c[agreementId]; return c; });
      setOpenSignFor(prev => { const c = { ...prev }; delete c[agreementId]; return c; });
      fetchPortalData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to sign'); }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await studentApi.get(`/api/student-auth/my-documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.document_name || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download document');
    }
  };

  const openPreview = async (doc) => {
    setPreview({ doc, url: null, kind: null, loading: true });
    try {
      const res = await studentApi.get(`/api/student-auth/my-documents/${doc.id}/download`, { responseType: 'blob' });
      const blob = res.data;
      const type = (blob.type || '').toLowerCase();
      const name = (doc.file_name || doc.document_name || '').toLowerCase();
      let kind = 'other';
      if (type.includes('pdf') || name.endsWith('.pdf')) kind = 'pdf';
      else if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/.test(name)) kind = 'image';
      if (kind === 'other') {
        // Not previewable inline — just download it
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = doc.file_name || 'document';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        setPreview(null);
        toast.info('This file type opens as a download');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      setPreview({ doc, url, kind, loading: false });
    } catch (err) {
      setPreview(null);
      toast.error(err.response?.data?.detail || 'Failed to open document');
    }
  };

  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const downloadSignedAgreement = async (ag) => {
    try {
      const res = await studentApi.get(`/api/student-auth/my-agreements/${ag.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeTitle = (ag.title || 'agreement').replace(/[^a-z0-9]/gi, '_').substring(0, 40);
      a.download = `${safeTitle}_signed.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download signed agreement');
    }
  };

  const getCurrentStageIndex = () => {
    if (!data?.portal?.current_stage) return 0;
    const idx = stageKeyToDisplayIdx(data.portal.current_stage);
    return idx >= 0 ? idx : 0;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8fafc' }}>
      <div className="text-center"><i className="fas fa-spinner fa-spin text-3xl text-[#192650]"></i><p className="mt-3 text-gray-500">Loading your portal...</p></div>
    </div>
  );

  const portal = data?.portal || {};
  const documents = data?.documents || [];

  // First-login onboarding gate — block the portal until contact details are captured.
  if (data && !portal.onboarding_completed && !student?.must_change_password) {
    return <StudentOnboarding studentApi={studentApi} portal={portal} onDone={fetchPortalData} />;
  }

  const summary = data?.summary || {};
  const currentStageIdx = getCurrentStageIndex();

  const pendingDocRequests = docRequests.filter(r => r.status === 'pending').length;
  const unreadMessages = messages.filter(m => !m.is_read && m.sender === 'staff').length;
  
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fa-home' },
    { id: 'documents', label: 'Documents', icon: 'fa-folder', badge: pendingDocRequests },
    { id: 'updates', label: 'Updates', icon: 'fa-bell', badge: updatesUnread },
    { id: 'payments', label: 'Payments', icon: 'fa-credit-card', badge: invoices.filter(i => i.status !== 'paid' && i.payment_status !== 'paid').length },
    { id: 'agreements', label: 'Agreements', icon: 'fa-file-signature', badge: agreements.filter(a => !a.signed).length },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#f1f5f9' }}>
      {/* Password Change Modal */}
      {showPasswordChange && (
        <PasswordChangeModal
          onClose={() => setShowPasswordChange(false)}
          onSave={async (newPw) => {
            await changePassword('', newPw);
          }}
        />
      )}

      {/* Header */}
      {/* Header + tab nav pinned together (single sticky container avoids the
          hardcoded-offset overlap bug on mobile) */}
      <div className="sticky top-0 z-50 shadow-lg">
      <header className="bg-[#192650] text-white" data-testid="student-portal-header">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
              <i className="fas fa-graduation-cap text-lg"></i>
            </div>
            <div>
              <h1 className="text-sm font-bold leading-tight">Student Portal</h1>
              <p className="text-[10px] text-white/60">Patrick's Edu Care</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/70 hidden sm:block">{student?.student_name}</span>
            <button
              onClick={togglePush}
              disabled={pushBusy}
              title={pushOn ? 'Alerts on — tap to turn off' : 'Turn on alerts for documents & agreements'}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${pushOn ? 'bg-emerald-500/90 hover:bg-emerald-500' : 'bg-white/10 hover:bg-white/20'} disabled:opacity-60`}
              data-testid="student-alerts-toggle-btn"
            >
              <i className={`fas ${pushBusy ? 'fa-spinner fa-spin' : (pushOn ? 'fa-bell' : 'fa-bell-slash')} mr-1`}></i>
              <span className="hidden sm:inline">{pushOn ? 'Alerts On' : 'Alerts'}</span>
            </button>
            <button onClick={() => setShowProfile(true)} className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors" data-testid="student-profile-btn">
              <i className="fas fa-user-circle mr-1"></i>Profile
            </button>
            <button onClick={logout} className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors" data-testid="student-logout-btn">
              <i className="fas fa-sign-out-alt mr-1"></i>Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tab Navigation (top strip on tablet/desktop; mobile uses the bottom bar) */}
      <nav className="bg-white border-b hidden sm:block">
        <div className="max-w-5xl mx-auto px-2 sm:px-4 flex gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`student-tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs font-medium border-b-2 whitespace-nowrap transition-colors min-h-[44px] flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-[#192650] text-[#192650]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`fas ${tab.icon}`}></i>
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-0.5 flex-shrink-0 inline-flex items-center justify-center min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold leading-none px-1 rounded-full">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>
      </nav>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-5 space-y-5 pb-24 sm:pb-5">

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            {/* Welcome */}
            <div className="bg-gradient-to-r from-[#192650] to-[#2a4080] rounded-xl p-5 text-white" data-testid="student-welcome">
              <h2 className="text-lg font-bold">Welcome, {student?.student_name}</h2>
              <p className="text-sm text-white/70 mt-1">
                {portal.university_name} — {portal.course || portal.program}
              </p>
              <div className="flex gap-4 mt-3">
                <div className="bg-white/10 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-white/60">Status</p>
                  <p className="text-sm font-semibold capitalize">{portal.status || 'Active'}</p>
                </div>
                <div className="bg-white/10 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-white/60">Intake</p>
                  <p className="text-sm font-semibold">{portal.intake || 'N/A'}</p>
                </div>
                <div className="bg-white/10 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-white/60">Country</p>
                  <p className="text-sm font-semibold">{portal.destination_country || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Stage Tracker */}
            <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="stage-tracker">
              <h3 className="text-sm font-bold text-gray-800 mb-4">
                <i className="fas fa-tasks mr-2 text-[#192650]"></i>Admission Progress
              </h3>
              <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
              <div className="flex items-start justify-between relative min-w-max">
                {/* Progress line */}
                <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-200"></div>
                <div className="absolute top-5 left-5 h-0.5 bg-[#192650] transition-all" style={{ width: `calc(${(currentStageIdx / Math.max(1, stages.length - 1)) * 100}% - 2.5rem)` }}></div>
                
                {stages.map((stage, idx) => {
                  // If this is the "visa_decision" slot and we have a concrete visa outcome, show it
                  let displayLabel = stage.label;
                  let displayIcon = stage.icon;
                  if (stage.key === 'visa_decision') {
                    const backendStage = data?.portal?.current_stage;
                    if (backendStage === 'visa_approved') { displayLabel = 'Visa Approved'; displayIcon = 'fa-check-circle'; }
                    else if (backendStage === 'visa_denied') { displayLabel = 'Visa Denied'; displayIcon = 'fa-times-circle'; }
                  }
                  return (
                    <div key={stage.key} className="relative flex flex-col items-center z-10 min-w-[64px] px-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm border-2 transition-all flex-shrink-0 ${
                        idx <= currentStageIdx
                          ? 'bg-[#192650] border-[#192650] text-white'
                          : 'bg-white border-gray-300 text-gray-400'
                      }`}>
                        {idx < currentStageIdx ? <i className="fas fa-check"></i> : <i className={`fas ${displayIcon}`}></i>}
                      </div>
                      <span className={`text-[9px] mt-1.5 text-center leading-tight ${idx <= currentStageIdx ? 'text-[#192650] font-semibold' : 'text-gray-400'}`}>
                        {displayLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: `${pendingDocRequests} Pending Docs`, icon: 'fa-folder-open', color: 'bg-blue-50 text-blue-600', tab: 'documents' },
                { label: 'Messages', icon: 'fa-comments', color: 'bg-green-50 text-green-600', tab: 'messages' },
                { label: 'Payments', icon: 'fa-credit-card', color: 'bg-amber-50 text-amber-600', tab: 'payments' },
                { label: 'Agreements', icon: 'fa-file-signature', color: 'bg-purple-50 text-purple-600', tab: 'agreements' },
              ].map((action, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTab(action.tab)}
                  className={`${action.color} rounded-xl p-4 text-left hover:shadow-md transition-all`}
                  data-testid={`quick-action-${i}`}
                >
                  <i className={`fas ${action.icon} text-xl mb-2`}></i>
                  <p className="text-xs font-semibold">{action.label}</p>
                </button>
              ))}
            </div>

            {/* Recent Activity */}
            {data?.communications?.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-3"><i className="fas fa-clock mr-2 text-gray-400"></i>Recent Updates</h3>
                <div className="space-y-3">
                  {data.communications.slice(0, 5).map((comm, i) => (
                    <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-comment text-xs text-gray-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-800">{comm.message || comm.content}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {comm.created_at ? new Date(comm.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === 'documents' && (
          <div className="space-y-4">
            {/* Pending Document Requests */}
            {docRequests.filter(r => r.status === 'pending').length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="pending-doc-requests">
                <h3 className="text-sm font-bold text-gray-800 mb-3">
                  <i className="fas fa-exclamation-circle mr-2 text-orange-500"></i>
                  Documents Requested ({docRequests.filter(r => r.status === 'pending').length})
                </h3>
                <div className="space-y-3">
                  {docRequests.filter(r => r.status === 'pending').map(req => (
                    <div key={req.id} className="p-3 border-2 border-orange-200 bg-orange-50 rounded-lg" data-testid={`doc-request-${req.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-800"><i className="fas fa-file-alt mr-1.5 text-orange-500"></i>{req.document_name}</p>
                          {req.instructions && <p className="text-xs text-gray-600 mt-0.5"><i className="fas fa-info-circle mr-1 text-gray-400"></i>{req.instructions}</p>}
                          <p className="text-[10px] text-gray-400 mt-1">Requested by {req.requested_by} | {req.created_at ? new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</p>
                        </div>
                      </div>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = new FormData();
                        const fileInput = e.target.querySelector('input[type="file"]');
                        if (!fileInput?.files?.[0]) { toast.error('Please select a file'); return; }
                        formData.append('file', fileInput.files[0]);
                        formData.append('request_id', req.id);
                        formData.append('document_type', req.document_name);
                        try {
                          await studentApi.post('/api/student-auth/my-documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          toast.success(`${req.document_name} uploaded!`);
                          fetchPortalData();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                      }} className="flex gap-2 mt-2">
                        <input type="file" className="text-xs flex-1" data-testid={`upload-file-${req.id}`} />
                        <button type="submit" className="bg-[#192650] text-white px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0" data-testid={`upload-btn-${req.id}`}>
                          <i className="fas fa-upload mr-1"></i>Upload
                        </button>
                      </form>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Uploaded Documents */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-3"><i className="fas fa-folder mr-2 text-blue-500"></i>Uploaded Documents ({documents.length})</h3>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="fas fa-folder-open text-3xl mb-2"></i>
                  <p className="text-sm">No documents uploaded yet</p>
                  {docRequests.filter(r => r.status === 'pending').length === 0 && <p className="text-xs mt-1">Your documentation team will request documents when needed</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  {[...documents].sort((a, b) => (b.shared_with_student ? 1 : 0) - (a.shared_with_student ? 1 : 0)).map(doc => (
                    <div key={doc.id} className={`flex items-center justify-between p-3 rounded-lg ${doc.shared_with_student ? 'border-2 border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-100' : 'border'}`} data-testid={`doc-item-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                          doc.shared_with_student ? 'bg-indigo-100' : doc.status === 'approved' ? 'bg-green-100' : doc.status === 'rejected' ? 'bg-red-100' : 'bg-amber-100'
                        }`}>
                          <i className={`fas ${doc.shared_with_student ? 'fa-file-arrow-down text-indigo-600' : doc.status === 'approved' ? 'fa-check-circle text-green-600' : doc.status === 'rejected' ? 'fa-times-circle text-red-600' : 'fa-clock text-amber-600'} text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-800 flex items-center gap-1.5 flex-wrap">
                            {doc.document_name || doc.document_type || doc.file_name}
                            {doc.shared_with_student && (
                              <span className="inline-flex items-center gap-1 bg-indigo-600 text-white text-[9px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5" data-testid={`shared-badge-${doc.id}`}>
                                <i className="fas fa-share-nodes"></i>Shared
                              </span>
                            )}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {doc.file_name} | {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}
                            {!doc.shared_with_student && (
                              <span className={`ml-2 font-semibold ${doc.status === 'approved' ? 'text-green-600' : doc.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}>
                                {doc.status === 'approved' ? 'Approved' : doc.status === 'rejected' ? 'Rejected' : 'Under Review'}
                              </span>
                            )}
                          </p>
                          {doc.status === 'rejected' && doc.rejection_reason && (
                            <p className="text-[10px] text-red-500 mt-0.5"><i className="fas fa-info-circle mr-1"></i>{doc.rejection_reason}</p>
                          )}
                          {doc.shared_with_student && (
                            <p className="text-[10px] text-indigo-600 mt-0.5 font-medium"><i className="fas fa-user-tie mr-1"></i>Shared by {doc.shared_by_name || 'our team'}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => openPreview(doc)}
                          className="text-[11px] font-semibold text-white bg-[#192650] rounded-lg px-2.5 py-1.5 hover:bg-[#243466]"
                          data-testid={`preview-doc-${doc.id}`}
                        >
                          <i className="fas fa-eye mr-1"></i>View
                        </button>
                        <button
                          onClick={() => downloadDocument(doc)}
                          className="text-[11px] font-semibold text-[#192650] border border-[#192650]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#192650]/5"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Completed Requests */}
            {docRequests.filter(r => r.status !== 'pending').length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm">
                <h3 className="text-xs font-semibold text-gray-500 mb-2">Completed Requests</h3>
                <div className="space-y-1">
                  {docRequests.filter(r => r.status !== 'pending').map(req => (
                    <div key={req.id} className="flex items-center gap-2 text-xs text-gray-500 py-1">
                      <i className={`fas ${req.status === 'approved' ? 'fa-check-circle text-green-500' : req.status === 'rejected' ? 'fa-times-circle text-red-500' : 'fa-cloud-upload-alt text-blue-500'}`}></i>
                      {req.document_name} — {req.status}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'updates' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="updates-section">
            <div className="p-4 border-b">
              <h3 className="text-sm font-bold text-gray-800"><i className="fas fa-bell mr-2 text-[#192650]"></i>Updates</h3>
              <p className="text-[10px] text-gray-500">Everything in one place — messages, document & payment requests, agreements and admission progress. Reply right here.</p>
            </div>

            {/* Unified timeline (chat-like) */}
            <div className="p-4 space-y-3 max-h-[520px] overflow-y-auto bg-slate-50" data-testid="updates-list">
              {updates.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <i className="fas fa-inbox text-3xl mb-2"></i>
                  <p className="text-sm">No updates yet.</p>
                </div>
              ) : (
                updates.map(item => {
                  const isStudent = item.sender === 'student';
                  const isSystem = item.sender === 'system';
                  const ts = item.ts ? new Date(item.ts).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

                  // System (admission progress) — centered pill
                  if (isSystem || item.type === 'stage_change') {
                    return (
                      <div key={item.id} className="flex justify-center" data-testid={`update-${item.type}-${item.id}`}>
                        <div className="bg-indigo-50 text-indigo-700 text-[11px] font-medium rounded-full px-3 py-1.5 border border-indigo-100">
                          <i className="fas fa-graduation-cap mr-1.5"></i>{item.title}{ts ? ` · ${ts}` : ''}
                        </div>
                      </div>
                    );
                  }

                  const bubbleBase = `max-w-[85%] rounded-xl px-3.5 py-2.5 shadow-sm ${isStudent ? 'bg-[#192650] text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm border border-gray-100'}`;
                  const meta = item.meta || {};

                  return (
                    <div key={item.id} className={`flex ${isStudent ? 'justify-end' : 'justify-start'}`} data-testid={`update-${item.type}-${item.id}`}>
                      <div className={bubbleBase}>
                        {!isStudent && <p className="text-[10px] font-semibold mb-0.5 text-gray-500">{item.sender_name}</p>}

                        {/* Type header chip */}
                        {item.type !== 'message' && (
                          <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${isStudent ? 'text-white/70' : 'text-[#192650]'}`}>
                            {item.type === 'document_request' && <><i className="fas fa-file-upload mr-1"></i>Document requested</>}
                            {item.type === 'document_uploaded' && <><i className="fas fa-file-alt mr-1"></i>Document</>}
                            {item.type === 'payment' && <><i className="fas fa-file-invoice mr-1"></i>Payment request</>}
                            {item.type === 'receipt' && <><i className="fas fa-receipt mr-1"></i>Payment receipt</>}
                            {item.type === 'agreement' && <><i className="fas fa-file-signature mr-1"></i>Agreement</>}
                          </p>
                        )}

                        {item.title && item.type !== 'message' && <p className="text-xs font-semibold leading-snug">{item.title}</p>}
                        {item.body && <p className="text-xs leading-relaxed mt-0.5 whitespace-pre-line">{item.body}</p>}
                        {item.type === 'payment' && meta.amount ? (
                          <p className="text-xs font-bold mt-1">₹{Number(meta.amount).toLocaleString('en-IN')} {meta.paid ? '· Paid ✓' : '· Due'}</p>
                        ) : null}
                        {item.type === 'receipt' && meta.status ? (
                          <p className={`text-[10px] mt-1 font-medium ${meta.status === 'verified' ? 'text-emerald-300' : meta.status === 'rejected' ? 'text-red-300' : (isStudent ? 'text-white/70' : 'text-amber-600')}`}>Status: {meta.status}</p>
                        ) : null}

                        {/* Inline reply actions */}
                        {item.type === 'document_request' && meta.status !== 'uploaded' && (
                          <label className="mt-2 inline-flex items-center gap-1.5 bg-white text-[#192650] border border-[#192650]/30 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold cursor-pointer hover:bg-[#192650]/5" data-testid={`update-upload-doc-${item.id}`}>
                            <i className={`fas ${uploadingDocFor === meta.request_id ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>
                            {uploadingDocFor === meta.request_id ? 'Uploading…' : 'Upload document'}
                            <input type="file" className="hidden" onChange={e => handleDocUploadForRequest(meta.request_id, e.target.files?.[0])} />
                          </label>
                        )}
                        {item.type === 'document_request' && meta.status === 'uploaded' && (
                          <p className="text-[10px] mt-1 text-emerald-600 font-medium"><i className="fas fa-check-circle mr-1"></i>Uploaded</p>
                        )}

                        {item.type === 'document_uploaded' && meta.document_id && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <button onClick={() => openPreview({ id: meta.document_id, file_name: meta.file_name, document_name: item.title })} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${isStudent ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-[#192650] text-white hover:bg-[#243466]'}`} data-testid={`update-preview-doc-${item.id}`}>
                              <i className="fas fa-eye"></i>View
                            </button>
                            <button onClick={() => downloadDocument({ id: meta.document_id, file_name: meta.file_name })} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${isStudent ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-white text-[#192650] border border-[#192650]/30 hover:bg-[#192650]/5'}`} data-testid={`update-download-doc-${item.id}`}>
                              <i className="fas fa-download"></i>
                            </button>
                          </div>
                        )}

                        {item.type === 'payment' && !meta.paid && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <button onClick={() => handlePayInvoice({ id: meta.invoice_id })} className="inline-flex items-center gap-1 bg-emerald-500 text-white rounded-lg px-2.5 py-1.5 text-[11px] font-semibold hover:bg-emerald-600" data-testid={`update-pay-${item.id}`}>
                              <i className="fas fa-bolt"></i>Pay now
                            </button>
                            <label className="inline-flex items-center gap-1 bg-white text-[#192650] border border-[#192650]/30 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold cursor-pointer hover:bg-[#192650]/5" data-testid={`update-receipt-${item.id}`}>
                              <i className={`fas ${uploadingReceiptFor === meta.invoice_id ? 'fa-spinner fa-spin' : 'fa-receipt'}`}></i>
                              {uploadingReceiptFor === meta.invoice_id ? 'Uploading…' : 'Upload receipt'}
                              <input type="file" accept="application/pdf,image/*" className="hidden" onChange={e => uploadInvoiceReceipt(meta.invoice_id, e.target.files?.[0])} />
                            </label>
                          </div>
                        )}

                        {item.type === 'agreement' && !meta.signed && (
                          <button onClick={() => { setActiveTab('agreements'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="mt-2 inline-flex items-center gap-1 bg-purple-500 text-white rounded-lg px-2.5 py-1.5 text-[11px] font-semibold hover:bg-purple-600" data-testid={`update-sign-${item.id}`}>
                            <i className="fas fa-pen-nib"></i>Review & sign
                          </button>
                        )}
                        {item.type === 'agreement' && meta.signed && (
                          <p className={`text-[10px] mt-1 font-medium ${isStudent ? 'text-white/70' : 'text-emerald-600'}`}><i className="fas fa-check-circle mr-1"></i>Signed</p>
                        )}

                        <p className={`text-[9px] mt-1 ${isStudent ? 'text-white/50' : 'text-gray-400'}`}>{ts}</p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={updatesEndRef}></div>
            </div>

            {/* Composer */}
            <div className="p-3 border-t bg-gray-50">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a message to our team…"
                  className="flex-1 border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#192650]/20"
                  data-testid="updates-message-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-[#192650] text-white px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-50"
                  data-testid="updates-send-btn"
                >
                  {sendingMessage ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paper-plane"></i>}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">Tip: reply to a document or payment request using the buttons on each card above.</p>
            </div>
          </div>
        )}

        {/* PAYMENTS TAB */}
        {activeTab === 'payments' && (
          <div className="space-y-4" data-testid="payments-section">
            {/* Mandatory: Unsigned Payment Schedule Agreement Banner */}
            {(() => {
              const unsignedPS = agreements.find(a => a.source === 'payment_schedule' && !a.signed);
              if (!unsignedPS) return null;
              return (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm" data-testid="unsigned-payment-schedule-banner">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <i className="fas fa-exclamation-triangle text-amber-700 text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-amber-900">Action required: Acknowledge your Payment Schedule</h4>
                      <p className="text-[12px] text-amber-800 mt-0.5 leading-snug">
                        Please review and digitally sign the Payment Schedule Acknowledgement in the <strong>Agreements</strong> tab. This confirms you understand all upcoming payments before we proceed.
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveTab('agreements')}
                      className="shrink-0 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg transition-colors self-start"
                      data-testid="go-sign-payment-schedule-btn"
                    >
                      <i className="fas fa-signature mr-1.5"></i>Review &amp; Sign
                    </button>
                  </div>
                </div>
              );
            })()}
            {/* Payment Roadmap Card */}
            <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="payment-roadmap-card">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-bold text-gray-800">
                  <i className="fas fa-map-signs mr-2 text-emerald-500"></i>Your Payment Roadmap
                </h3>
                {paymentSchedule?.has_schedule && (
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                    {paymentSchedule.country} · {paymentSchedule.program}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                Overview of all payments in your admission journey. <strong>You only pay when an official invoice is raised</strong> — use this to plan ahead.
              </p>
              {!paymentSchedule?.has_schedule ? (
                <div className="text-center py-6 text-gray-400 border-2 border-dashed rounded-lg bg-gray-50" data-testid="no-schedule-msg">
                  <i className="fas fa-clock text-2xl mb-2"></i>
                  <p className="text-xs max-w-md mx-auto">
                    {paymentSchedule?.message || 'Payment schedule not yet published for your country/program. Your counselor will share it soon.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {paymentSchedule.items.map((it, idx) => {
                      const statusConfig = {
                        planned: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-600', icon: 'fa-clock', label: 'Planned' },
                        invoiced: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', icon: 'fa-file-invoice', label: 'Invoiced' },
                        paid: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', icon: 'fa-check-circle', label: 'Paid' },
                      }[it.status] || { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-600', icon: 'fa-clock', label: 'Planned' };
                      const payeeLabels = {
                        indian_company: 'Indian Company Account',
                        university: 'University Account',
                        hostel: 'Hostel Account',
                        other: 'Other',
                        company: 'Indian Company Account',
                        institution: 'Other',
                      };
                      const currencySymbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$' };
                      const itemCurrency = (it.currency || paymentSchedule.currency || 'INR').toUpperCase();
                      const itemSym = currencySymbols[itemCurrency] || itemCurrency + ' ';
                      const showInrEq = itemCurrency !== 'INR' && (Number(it.amount_inr) > 0);
                      return (
                        <div
                          key={it.id || idx}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${statusConfig.bg} ${statusConfig.border}`}
                          data-testid={`schedule-item-${idx}`}
                        >
                          <div className="shrink-0 w-8 h-8 rounded-full bg-white border-2 border-current flex items-center justify-center text-xs font-bold text-gray-700 mt-0.5">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                              <h4 className="text-sm font-semibold text-gray-800">{it.title}</h4>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusConfig.badge}`}>
                                <i className={`fas ${statusConfig.icon} mr-1`}></i>{statusConfig.label}
                              </span>
                              {it.due_label && (
                                <span className="inline-flex items-center text-[10px] text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                                  <i className="far fa-calendar-alt mr-1"></i>{it.due_label}
                                </span>
                              )}
                            </div>
                            {it.description && <p className="text-[11px] text-gray-600 leading-snug mb-1">{it.description}</p>}
                            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                              <span>
                                <i className="fas fa-university mr-1 text-gray-400"></i>
                                Pay to: <strong className="text-gray-700">{payeeLabels[it.payee_type] || 'Other'}</strong>
                              </span>
                              {it.receipt_number && <span className="text-emerald-700">Receipt: {it.receipt_number}</span>}
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-sm font-bold text-gray-900" data-testid={`schedule-item-${idx}-amount`}>
                              {itemSym}{Number(it.amount || 0).toLocaleString('en-IN')}
                            </div>
                            {showInrEq && (
                              <div className="text-[10px] text-gray-500 mt-0.5" data-testid={`schedule-item-${idx}-inr`}>
                                (≈ ₹{Number(it.amount_inr).toLocaleString('en-IN', { maximumFractionDigits: 0 })})
                              </div>
                            )}
                            {it.status === 'paid' && it.received_amount && (
                              <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">
                                <i className="fas fa-check mr-0.5"></i>Received
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    <span className="text-xs text-gray-600">Total Initial Fees (INR equivalent)</span>
                    <span className="text-lg font-bold text-emerald-700" data-testid="schedule-total-inr">
                      ₹{Number(paymentSchedule.total_inr || paymentSchedule.total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="mt-2 rounded-lg bg-blue-50 border border-blue-200 p-2.5 flex items-start gap-2" data-testid="payment-gst-fx-note">
                    <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                    <p className="text-[11px] text-blue-800 leading-snug">
                      Payments to the <strong>Indian Company Account</strong> attract <strong>18% GST</strong>. Foreign-currency amounts are transferred at the exchange rate on the day of payment.
                    </p>
                  </div>
                  <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex items-start gap-2">
                    <i className="fas fa-shield-alt text-amber-600 mt-0.5"></i>
                    <p className="text-[11px] text-amber-800 leading-snug">
                      <strong>Safety note:</strong> Pay only against official invoices raised by our finance team. Do not transfer to any personal account.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Invoices & Receipts */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-3"><i className="fas fa-credit-card mr-2 text-amber-500"></i>Invoices &amp; Receipts</h3>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="fas fa-receipt text-3xl mb-2"></i>
                  <p className="text-sm">No invoices or receipts yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map(item => {
                    const total = item.total_amount || item.grand_total || item.amount || item.net_amount || item.base_amount || 0;
                  const isPaid = item.status === 'paid' || item.payment_status === 'paid' || item.status === 'received';
                  const isReceipt = item.document_type === 'receipt';
                  const docNumber = item.document_number || item.invoice_number || item.id?.slice(0, 8);
                  // For paid invoices, we have receipt_payment_id pointing to the enhanced_payment receipt
                  const receiptPaymentId = item.receipt_payment_id || (isReceipt ? item.payment_id : null);
                  const bankReceipts = paymentReceipts.filter(r => r.invoice_id === item.id);
                  const hasPendingReceipt = bankReceipts.some(r => r.status === 'pending');
                  
                  const downloadReceipt = async (paymentId) => {
                    try {
                      const res = await studentApi.get(`/api/student-auth/my-receipt/${paymentId}/download`, { responseType: 'blob' });
                      const blob = new Blob([res.data], { type: 'application/pdf' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Receipt_${item.receipt_number || docNumber}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch {
                      toast.error('Failed to download receipt');
                    }
                  };
                  
                  return (
                    <div key={item.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg ${isPaid ? 'bg-green-50 border-green-200' : isReceipt ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} data-testid={`payment-${item.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPaid ? 'bg-green-100' : isReceipt ? 'bg-blue-100' : 'bg-amber-100'}`}>
                          <i className={`fas ${isReceipt ? 'fa-receipt text-blue-600' : isPaid ? 'fa-check-circle text-green-600' : 'fa-file-invoice text-amber-600'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{docNumber}</p>
                          <p className="text-xs text-gray-500">
                            {isReceipt ? 'Receipt' : 'Invoice'} | Amount: <span className="font-bold text-gray-800">&#8377;{Number(total).toLocaleString('en-IN')}</span>
                            {item.description && <> | {item.description}</>}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 sm:mt-0">
                        {isReceipt ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 text-xs font-semibold">
                              <i className="fas fa-receipt mr-1.5"></i>Receipt
                            </span>
                            {receiptPaymentId && (
                              <button
                                onClick={() => downloadReceipt(receiptPaymentId)}
                                className="px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100"
                                data-testid={`download-receipt-${item.id}`}
                              >
                                <i className="fas fa-download mr-1"></i>Download
                              </button>
                            )}
                          </div>
                        ) : isPaid ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-xs font-semibold">
                              <i className="fas fa-check-circle mr-1.5"></i>Paid
                            </span>
                            {receiptPaymentId && (
                              <button
                                onClick={() => downloadReceipt(receiptPaymentId)}
                                className="px-3 py-1.5 border border-green-300 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100"
                                data-testid={`download-receipt-for-${item.id}`}
                              >
                                <i className="fas fa-download mr-1"></i>Receipt
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2 mt-2 sm:mt-0 sm:items-end">
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await studentApi.get(`/api/student-auth/my-invoices/${item.id}/download`, { responseType: 'blob' });
                                    const blob = new Blob([res.data], { type: 'application/pdf' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url; a.download = `${docNumber}.pdf`; a.click();
                                    URL.revokeObjectURL(url);
                                  } catch { toast.error('Failed to download invoice'); }
                                }}
                                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50"
                                data-testid={`download-invoice-${item.id}`}
                              >
                                <i className="fas fa-download mr-1"></i>Download
                              </button>
                              <button
                                onClick={() => handlePayInvoice(item)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
                                data-testid={`pay-btn-${item.id}`}
                              >
                                <i className="fas fa-credit-card mr-1.5"></i>Pay Online &#8377;{Number(total).toLocaleString('en-IN')}
                              </button>
                            </div>
                            {/* Or upload bank receipt for manual payment */}
                            <div className="flex items-center gap-2 text-[11px] text-gray-500 self-end">
                              <span className="italic">or</span>
                              <label
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md cursor-pointer transition-colors ${
                                  uploadingReceiptFor === item.id
                                    ? 'bg-gray-100 text-gray-400 pointer-events-none'
                                    : hasPendingReceipt
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                                    : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50'
                                }`}
                                data-testid={`upload-invoice-receipt-${item.id}`}
                              >
                                {uploadingReceiptFor === item.id ? (
                                  <><i className="fas fa-spinner fa-spin"></i> Uploading...</>
                                ) : hasPendingReceipt ? (
                                  <><i className="fas fa-hourglass-half"></i> Receipt pending verification</>
                                ) : (
                                  <><i className="fas fa-upload"></i> Paid offline? Upload Bank Receipt</>
                                )}
                                <input
                                  type="file"
                                  accept=".pdf,image/png,image/jpeg,image/webp"
                                  className="hidden"
                                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadInvoiceReceipt(item.id, f); e.target.value = ''; }}
                                  disabled={uploadingReceiptFor === item.id || hasPendingReceipt}
                                />
                              </label>
                              {/* Snap the receipt with the phone camera (mobile) */}
                              <label
                                className={`sm:hidden flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                                  uploadingReceiptFor === item.id || hasPendingReceipt
                                    ? 'bg-gray-100 text-gray-400 pointer-events-none'
                                    : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50'
                                }`}
                                data-testid={`camera-invoice-receipt-${item.id}`}
                              >
                                <i className="fas fa-camera"></i> Snap
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  className="hidden"
                                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadInvoiceReceipt(item.id, f); e.target.value = ''; }}
                                  disabled={uploadingReceiptFor === item.id || hasPendingReceipt}
                                />
                              </label>
                              <span className="text-[10px] text-gray-400">PDF / PNG / JPG · ≤5 MB</span>
                            </div>
                            {bankReceipts.length > 0 && (
                              <div className="space-y-1 self-end">
                                {bankReceipts.map(r => (
                                  <div key={r.id} className="flex items-center gap-2 text-[11px] bg-gray-50 rounded border border-gray-200 px-2 py-1" data-testid={`receipt-row-${r.id}`}>
                                    <i className={`fas ${r.content_type === 'application/pdf' ? 'fa-file-pdf text-red-500' : 'fa-file-image text-blue-500'}`}></i>
                                    <span className="truncate max-w-[180px] text-gray-700">{r.file_name}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                      r.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                                      r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                    }`}>
                                      {r.status || 'pending'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>
        )}

        {/* AGREEMENTS TAB */}
        {activeTab === 'agreements' && (
          <div className="space-y-4" data-testid="agreements-section">
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-3"><i className="fas fa-file-signature mr-2 text-indigo-500"></i>Agreements & Consent Forms</h3>
              {agreements.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="fas fa-file-contract text-3xl mb-2"></i>
                  <p className="text-sm">No agreements pending</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {agreements.map(ag => {
                    const isChecked = !!consentChecked[ag.id];
                    const isHtml = typeof ag.content === 'string' && /<\s*(table|h\d|div|p|ul|ol|tr|td|style)\b/i.test(ag.content);
                    const isMandatory = ag.is_mandatory || ag.source === 'payment_schedule';
                    const isExpanded = expandedAgreementId === ag.id;
                    const toggle = () => setExpandedAgreementId(prev => prev === ag.id ? null : ag.id);
                    return (
                    <div key={ag.id} className={`border rounded-lg overflow-hidden ${ag.signed ? 'bg-green-50 border-green-200' : isMandatory ? 'bg-amber-50/30 border-amber-300' : 'bg-white border-gray-200'}`} data-testid={`agreement-${ag.id}`}>
                      {/* Compact list header — always visible, clickable to toggle */}
                      <button
                        type="button"
                        onClick={toggle}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-black/[0.02] transition-colors"
                        data-testid={`agreement-toggle-${ag.id}`}
                        aria-expanded={isExpanded}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <i className={`fas fa-file-signature text-base ${ag.signed ? 'text-green-600' : isMandatory ? 'text-amber-500' : 'text-indigo-500'}`}></i>
                          <h4 className="text-sm font-semibold text-gray-800 truncate" title={ag.title}>{ag.title}</h4>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {ag.signed ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold">
                              <i className="fas fa-check-circle mr-1"></i>Signed{ag.signed_at ? ` · ${new Date(ag.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${isMandatory ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`} data-testid={`status-badge-${ag.id}`}>
                              <i className={`fas ${isMandatory ? 'fa-exclamation-circle' : 'fa-hourglass-half'} mr-1`}></i>
                              {isMandatory ? 'Mandatory · Pending' : 'Pending'}
                            </span>
                          )}
                          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-xs text-gray-400`}></i>
                        </div>
                      </button>

                      {/* Expanded body — content + sign flow */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 p-4 space-y-3" data-testid={`agreement-body-${ag.id}`}>
                          {ag.content && (
                            isHtml ? (
                              <div
                                className="text-xs text-gray-700 max-h-96 overflow-y-auto p-3 bg-gray-50 rounded border"
                                data-testid={`agreement-content-${ag.id}`}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(ag.content, {
                                    USE_PROFILES: { html: true },
                                    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
                                    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
                                  })
                                }}
                              />
                            ) : (
                              <div className="text-xs text-gray-700 whitespace-pre-line max-h-60 overflow-y-auto p-3 bg-gray-50 rounded border" data-testid={`agreement-content-${ag.id}`}>{ag.content}</div>
                            )
                          )}
                          {!ag.signed && (
                            <div className="pt-3 border-t border-dashed border-gray-200">
                              {!openSignFor[ag.id] ? (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <p className="text-[11px] text-gray-500 leading-snug">
                                    <i className="fas fa-info-circle mr-1 text-gray-400"></i>
                                    Read the contents above, then click to sign with your finger or mouse.
                                  </p>
                                  <button
                                    onClick={() => setOpenSignFor(prev => ({ ...prev, [ag.id]: true }))}
                                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${isMandatory ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-[#192650] text-white hover:bg-[#0f1a38]'}`}
                                    data-testid={`open-sign-${ag.id}`}
                                  >
                                    <i className="fas fa-signature"></i>
                                    <span>Sign Agreement</span>
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="text-[11px] font-semibold text-gray-700">
                                        <i className="fas fa-pen mr-1.5 text-indigo-500"></i>Draw your signature below
                                      </div>
                                      <button
                                        onClick={() => {
                                          setOpenSignFor(prev => { const c = { ...prev }; delete c[ag.id]; return c; });
                                          setSignatures(prev => { const c = { ...prev }; delete c[ag.id]; return c; });
                                          setConsentChecked(prev => { const c = { ...prev }; delete c[ag.id]; return c; });
                                        }}
                                        className="text-[11px] text-gray-500 hover:text-gray-700 underline"
                                        data-testid={`cancel-sign-${ag.id}`}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                    <SignaturePad
                                      testId={`sig-pad-${ag.id}`}
                                      onChange={(dataUrl) => setSignatures(prev => ({ ...prev, [ag.id]: dataUrl }))}
                                    />
                                  </div>
                                  <label className="flex items-start gap-2.5 cursor-pointer select-none group" data-testid={`consent-label-${ag.id}`}>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={(e) => setConsentChecked(prev => ({ ...prev, [ag.id]: e.target.checked }))}
                                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#192650] focus:ring-[#192650] cursor-pointer"
                                      data-testid={`consent-checkbox-${ag.id}`}
                                    />
                                    <span className="text-xs text-gray-700 leading-snug group-hover:text-gray-900">
                                      I have read, understood and agree to the contents above. My digital signature above is binding.
                                    </span>
                                  </label>
                                  <button
                                    onClick={() => handleSignAgreement(ag.id)}
                                    disabled={!isChecked || !signatures[ag.id]}
                                    className={`w-full sm:w-auto px-5 py-2 rounded-lg text-xs font-semibold transition-colors ${(isChecked && signatures[ag.id]) ? 'bg-[#192650] text-white hover:bg-[#0f1a38]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                                    data-testid={`sign-agreement-${ag.id}`}
                                  >
                                    <i className="fas fa-signature mr-1.5"></i>Submit Signature
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {ag.signed && (
                            <div className="pt-3 border-t border-dashed border-green-200 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-[11px] text-gray-500 leading-snug">
                                <i className="fas fa-shield-alt mr-1 text-green-500"></i>
                                Audit-ready PDF includes your signature, IP address, device info and timestamp.
                              </p>
                              <button
                                onClick={() => downloadSignedAgreement(ag)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-green-300 text-green-700 bg-white hover:bg-green-50 transition-colors"
                                data-testid={`download-signed-${ag.id}`}
                              >
                                <i className="fas fa-file-pdf"></i>
                                <span>Download Signed PDF</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Mobile bottom tab bar — thumb-friendly (replaces the top strip on phones) */}      <nav
        className="sm:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-200 flex justify-around items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="student-bottom-nav"
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            data-testid={`student-bottomtab-${tab.id}`}
            className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
              activeTab === tab.id ? 'text-[#192650]' : 'text-gray-400'
            }`}
          >
            <span className="relative">
              <i className={`fas ${tab.icon} text-base`}></i>
              {tab.badge > 0 && (
                <span className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[15px] h-[15px] bg-red-500 text-white text-[8px] font-bold leading-none px-1 rounded-full">{tab.badge > 99 ? '99+' : tab.badge}</span>
              )}
            </span>
            <span className="text-[9px] font-medium leading-none">{tab.label}</span>
            {activeTab === tab.id && <span className="absolute top-0 inset-x-4 h-0.5 bg-[#192650] rounded-full"></span>}
          </button>
        ))}
      </nav>

      {showProfile && (
        <StudentOnboarding
          studentApi={studentApi}
          portal={portal}
          editMode={true}
          onDone={fetchPortalData}
          onClose={() => setShowProfile(false)}
        />
      )}

      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex flex-col" data-testid="document-preview-modal">
          <div className="flex items-center justify-between px-4 py-3 bg-[#192650] text-white">
            <div className="min-w-0 flex-1 mr-3">
              <p className="text-sm font-semibold truncate">{preview.doc.document_name || preview.doc.file_name || 'Document'}</p>
              {preview.doc.shared_by_name && <p className="text-[10px] text-white/70 truncate">Shared by {preview.doc.shared_by_name}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => downloadDocument(preview.doc)} className="text-xs bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5" data-testid="preview-download-btn">
                <i className="fas fa-download mr-1"></i><span className="hidden sm:inline">Download</span>
              </button>
              <button onClick={closePreview} className="text-xs bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5" data-testid="preview-close-btn">
                <i className="fas fa-times mr-1"></i><span className="hidden sm:inline">Close</span>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-center justify-center p-2 sm:p-4">
            {preview.loading ? (
              <div className="text-white text-center"><i className="fas fa-spinner fa-spin text-2xl mb-2"></i><p className="text-sm">Loading document…</p></div>
            ) : preview.kind === 'pdf' ? (
              <iframe src={preview.url} title="Document preview" className="w-full h-full bg-white rounded" data-testid="preview-pdf-frame"></iframe>
            ) : (
              <img src={preview.url} alt="Document preview" className="max-w-full max-h-full object-contain rounded shadow-lg" data-testid="preview-image" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentPortalDashboard;
